import { Users, TrendingUp, Briefcase, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SupervisaoHeaderProps {
  resumo: {
    equipeTamanho: number
    reunioesTotal: number
    produzidoTotal: number
    produzidoVinculado: number
    contratosAguardandoVinculo: number
  }
}

export function SupervisaoHeader({ resumo }: SupervisaoHeaderProps) {
  const percVinculados =
    resumo.produzidoTotal > 0
      ? Math.round((resumo.produzidoVinculado / resumo.produzidoTotal) * 100)
      : 0

  const cards = [
    {
      title: 'Equipe',
      value: resumo.equipeTamanho,
      description: 'Relacionadores ativos',
      colorClass: 'text-[#FF3737] dark:text-[#FF3737]',
      iconBgClass: 'bg-[#FF3737]/10 dark:bg-[#FF3737]/15',
      icon: Users,
    },
    {
      title: 'Reuniões',
      value: resumo.reunioesTotal,
      description: 'No período selecionado',
      colorClass: 'text-[#2563eb] dark:text-[#60a5fa]',
      iconBgClass: 'bg-[#2563eb]/10 dark:bg-[#60a5fa]/15',
      icon: Briefcase,
    },
    {
      title: 'Produção',
      value: resumo.produzidoTotal,
      description:
        resumo.produzidoTotal === 0 ? 'Sem contratos no período' : `${percVinculados}% vinculados`,
      colorClass:
        resumo.produzidoTotal === 0
          ? 'text-[#dc2626] dark:text-[#f87171]'
          : 'text-[#d97706] dark:text-[#fbbf24]',
      iconBgClass:
        resumo.produzidoTotal === 0
          ? 'bg-[#dc2626]/10 dark:bg-[#f87171]/15'
          : 'bg-[#d97706]/10 dark:bg-[#fbbf24]/15',
      icon: TrendingUp,
      warn: resumo.produzidoTotal === 0,
    },
    ...(resumo.contratosAguardandoVinculo > 0
      ? [
          {
            title: 'Sem vínculo',
            value: resumo.contratosAguardandoVinculo,
            description: 'Contratos aguardando vínculo',
            colorClass: 'text-[#ca8a04] dark:text-[#facc15]',
            iconBgClass: 'bg-[#ca8a04]/10 dark:bg-[#facc15]/15',
            icon: AlertCircle,
            warn: true,
          },
        ]
      : []),
  ]

  return (
    <div
      className={cn(
        'grid gap-4',
        cards.length >= 4
          ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4'
          : 'grid-cols-1 sm:grid-cols-3'
      )}
    >
      {cards.map((card) => {
        const Icon = card.icon
        return (
          <div
            key={card.title}
            className={cn(
              'rounded-[20px] bg-white/70 dark:bg-zinc-900/60 backdrop-blur-md border shadow-sm p-5 hover:shadow-md hover:translate-y-[-2px] transition-all duration-300',
              card.warn
                ? 'border-amber-200/80 dark:border-amber-500/20'
                : 'border-[rgba(255,55,55,0.1)] dark:border-white/10'
            )}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div
                className={cn('p-2.5 rounded-[12px] flex-shrink-0', card.iconBgClass, card.colorClass)}
              >
                <Icon className="h-4 w-4" />
              </div>
            </div>
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
                {card.title}
              </p>
              <p className={cn('text-2xl font-bold tracking-tight', card.colorClass)}>
                {card.value}
              </p>
              <p className="text-xs text-[#64748b]">{card.description}</p>
            </div>
          </div>
        )
      })}
    </div>
  )
}
